import React from "react";
import { Redirect } from "react-router-dom";
import axios from "axios";
import PropTypes from "prop-types";
import * as Yup from "yup";
import { t } from "@lingui/macro";
import { I18n } from "@lingui/react";
import { FieldText, Button } from "@conserveit/blocks";
import { withFormik } from "formik";
import { Wrapper } from "../Login/Login.styles";
import {
  FormContainer,
  Header,
  Content,
  Footer,
  ErrorMessage,
} from "./AuthForm.styles";

function getValidationMessages(i18n) {
  return {
    noMatch: i18n._(
      t("reset_password.passwords_dont_match")`Passwords don't match`
    ),
    required: i18n._(
      t("reset_password.password_required")`Field cannot be blank`
    ),
  };
}

const ResetPassword = ({
  values,
  status,
  errors,
  handleChange,
  handleBlur,
  handleSubmit,
  isSubmitting,
}) => {
  if (status.success) {
    return (
      <Redirect
        to={{
          pathname: "/login",
          fromPasswordResetSuccess: true,
        }}
      />
    );
  }
  return (
    <Wrapper>
      <div className="Wrapper-inner">
        <I18n>
          {({ i18n }) => (
            <FormContainer onSubmit={handleSubmit}>
              <Header data-cy="AuthForm-header">
                {i18n._(
                  t("reset_password.reset_your_password")`Reset your password`
                )}
              </Header>
              <Content>
                {errors.serverError && (
                  <ErrorMessage>{errors.serverError}</ErrorMessage>
                )}
                {errors.token && <ErrorMessage>{errors.token}</ErrorMessage>}

                <FieldText
                  name="password"
                  type="password"
                  onChange={handleChange}
                  error={getValidationMessages(i18n)[errors.password]}
                  value={values.password}
                  onBlur={handleBlur}
                  disabled={isSubmitting || status.success}
                  label={i18n._(
                    t("reset_password.new_password_label")`New password`
                  )}
                />

                <FieldText
                  name="passwordConfirm"
                  type="password"
                  onChange={handleChange}
                  error={getValidationMessages(i18n)[errors.passwordConfirm]}
                  value={values.passwordConfirm}
                  onBlur={handleBlur}
                  disabled={isSubmitting || status.success}
                  label={i18n._(
                    t(
                      "reset_password.confirm_new_password_label"
                    )`Confirm new password`
                  )}
                />
              </Content>
              <Footer>
                <Button type="submit" primary disabled={isSubmitting}>
                  {i18n._(
                    t("reset_password.set_new_password")`Set new password`
                  )}
                </Button>
              </Footer>
            </FormContainer>
          )}
        </I18n>
      </div>
    </Wrapper>
  );
};

ResetPassword.propTypes = {
  status: PropTypes.shape({
    success: PropTypes.string,
  }),
  values: PropTypes.shape({
    password: PropTypes.string.isRequired,
    passwordConfirm: PropTypes.string.isRequired,
  }).isRequired,
  errors: PropTypes.shape({
    password: PropTypes.string,
    passwordConfirm: PropTypes.string,
  }).isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};

ResetPassword.defaultProps = {
  status: {},
};

export default withFormik({
  validateOnBlur: false,
  validateOnChange: false,
  handleSubmit: (values, { setSubmitting, setErrors, setStatus }) => {
    axios
      .post("/auth/reset", {
        new_password: values.password,
        token: values.token,
      })
      .then(({ data }) => setStatus({ success: data.message }))
      .catch((error) => setErrors({ serverError: error.response.data.message }))
      .then(() => setSubmitting(false));
  },
  mapPropsToValues: () => ({
    password: "",
    passwordConfirm: "",
    token: new URL(window.location).searchParams.get("token") || "",
  }),
  validationSchema: Yup.object({
    password: Yup.string().required("required"),
    passwordConfirm: Yup.string()
      .required("required")
      .test("match", "noMatch", function comparePassword(passwordConfirm) {
        return passwordConfirm === this.parent.password;
      }),
    token: Yup.string().required(),
  }),
})(ResetPassword);
